import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoPaginationResponse } from '@webapp/infrastructure/interfaces/repositories/RepoPaginationResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import {
  type CreateCreditCardItemPayload,
  type ICreditCardItemRepository,
  type ListCreditCardItemsParams,
  type ListCreditCardItemsResult,
  type ListUnmatchedCreditCardItemsParams,
  type UpdateCreditCardItemPayload,
} from '@webapp/modules/accounts-payable/business-logic/interfaces/ICreditCardItemRepository'
import { type CreditCardItemConstructor } from '@webapp/modules/accounts-payable/entities/CreditCardItem'
import { type RelatedItemsResult } from './CreditCardItemRepository.types'
import { type CreditCardItemApiModel } from '../../api-models/CreditCardItemApiModel'
import { mapCreditCardItemApiModel } from '../../mappers/mapCreditCardItemApiModel'

interface Dependency {
  httpClient: IHttpClient
}

interface ApiPaginationApiModel {
  count?: number
  page_size?: number
  num_pages?: number
  current_page?: number
}

interface ListCreditCardItemsResponse {
  data: CreditCardItemApiModel[]
  metadata?: { pagination?: ApiPaginationApiModel }
}

interface RelatedItemsResponse {
  data: {
    id: number
    related_items: { id: number }[]
    selected_items: { id: number }[]
  }[]
}

const BASE_URL = '/api/v2/ap/creditcard-items/'

const mapPagination = (
  pagination: ApiPaginationApiModel | undefined
): RepoPaginationResponse => ({
  count: pagination?.count ?? 0,
  pageSize: pagination?.page_size ?? 10,
  numPages: pagination?.num_pages ?? 1,
  page: pagination?.current_page ?? 1,
})

// The v2 endpoint expects Python-style capitalized booleans (verified live:
// `archived=False&pending_reconcile=True`).
const toApiBoolean = (value: boolean) => (value ? 'True' : 'False')

export class CreditCardItemRepository implements ICreditCardItemRepository {
  constructor(private readonly deps: Dependency) {}

  async list(
    params: ListCreditCardItemsParams
  ): Promise<ListCreditCardItemsResult> {
    try {
      const res = await this.deps.httpClient.get<ListCreditCardItemsResponse>(
        BASE_URL,
        {
          params: {
            creditcard: params.creditcardId,
            archived: toApiBoolean(params.archived),
            page: params.page ?? 1,
            page_size: params.pageSize ?? 10,
            pending_reconcile: toApiBoolean(true),
          },
        }
      )

      return {
        items: res.data.data.map(mapCreditCardItemApiModel),
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async listUnmatched(
    params: ListUnmatchedCreditCardItemsParams
  ): Promise<ListCreditCardItemsResult> {
    try {
      const res = await this.deps.httpClient.get<ListCreditCardItemsResponse>(
        `${BASE_URL}unmatched/`,
        {
          params: {
            creditcard: params.creditcardId,
            order_by: params.orderBy ?? '-item__received_on',
            page: params.page ?? 1,
            page_size: params.pageSize ?? 10,
            pending_reconcile: toApiBoolean(true),
            // Pane search box (BHV-052)
            ...(params.search ? { search: params.search } : {}),
          },
        }
      )

      return {
        items: res.data.data.map(mapCreditCardItemApiModel),
        pagination: mapPagination(res.data.metadata?.pagination),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getRelatedItems(itemIds: number[]): Promise<RelatedItemsResult[]> {
    try {
      // Comma-joined ids in the detail segment (verified live).
      const res = await this.deps.httpClient.get<RelatedItemsResponse>(
        `${BASE_URL}${itemIds.join(',')}/related-items/`
      )

      return res.data.data.map((row) => ({
        id: row.id,
        relatedItemIds: row.related_items.map((related) => related.id),
        selectedItemIds: row.selected_items.map((selected) => selected.id),
      }))
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async create(
    payload: CreateCreditCardItemPayload
  ): Promise<CreditCardItemConstructor> {
    try {
      const res = await this.deps.httpClient.post<{
        data: CreditCardItemApiModel
      }>(BASE_URL, payload)

      return mapCreditCardItemApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async update(
    id: number,
    payload: UpdateCreditCardItemPayload
  ): Promise<CreditCardItemConstructor> {
    try {
      const res = await this.deps.httpClient.patch<{
        data: CreditCardItemApiModel
      }>(`${BASE_URL}${id}/`, payload)

      return mapCreditCardItemApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async updateCostAndCurrency(
    id: number,
    payload: { totalCost: string; currencyId: number }
  ): Promise<CreditCardItemConstructor> {
    try {
      const res = await this.deps.httpClient.patch<{
        data: CreditCardItemApiModel
      }>(`${BASE_URL}${id}/`, {
        id,
        total_cost: payload.totalCost,
        currency: payload.currencyId,
      })

      return mapCreditCardItemApiModel(res.data.data)
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async setArchived(id: number, archived: boolean): Promise<void> {
    try {
      // Response is the full item read serializer — discarded; lists refetch.
      await this.deps.httpClient.patch(`${BASE_URL}${id}/archive/`, {
        archived,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async moveToCreditCard(
    targetCreditCardId: number,
    itemIds: number[]
  ): Promise<void> {
    try {
      // Collection-route batch PATCH; item ids are serialized as strings
      // (verified Angular payload).
      await this.deps.httpClient.patch(BASE_URL, {
        creditcard: targetCreditCardId,
        creditcard_items: itemIds.map(String),
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async attachInvoice(
    itemId: number,
    packingSlipUuids: string[]
  ): Promise<void> {
    try {
      await this.deps.httpClient.patch(`${BASE_URL}${itemId}/`, {
        packing_slips: packingSlipUuids,
      })
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
